import { z } from 'zod';
import type {
  ArticleBlock,
  EditionDetail,
  EditionList,
  PublicArticle,
  PublicArticleSummary,
  RevisionContent,
} from './article-types';

/**
 * zod schemas for the newsroom payloads (contract §2–§4) — the trust
 * boundary between the backend and both the public research pages and the
 * admin preview.
 *
 * Blocks are parsed one by one: a block of a type this frontend does not know
 * yet (or a malformed one) is dropped and counted, never allowed to 404 the
 * whole article. Optional text fields accept null or absence alike.
 */

const text = z.string();
const optionalText = z.string().nullish();

const headingSchema = z.object({ type: z.enum(['h2', 'h3']), text });
const paragraphSchema = z.object({ type: z.literal('p'), text });
const listSchema = z.object({
  type: z.literal('list'),
  ordered: z.boolean().default(false),
  items: z.array(text).min(1),
});
const figureSchema = z.object({
  type: z.literal('figure'),
  asset: text,
  alt: text,
  caption: optionalText,
  source: optionalText,
});
const keyFiguresSchema = z.object({
  type: z.literal('keyfigures'),
  items: z.array(z.object({ value: text, label: text })).min(1),
});
const tableSchema = z.object({
  type: z.literal('table'),
  caption: optionalText,
  columns: z.array(text).min(1),
  rows: z.array(z.array(text)),
  align: z.array(z.enum(['left', 'right'])).nullish(),
});
const noteSchema = z.object({ type: z.literal('note'), label: optionalText, text });
const hrSchema = z.object({ type: z.literal('hr') });

export const articleBlockSchema = z.union([
  headingSchema,
  paragraphSchema,
  listSchema,
  figureSchema,
  keyFiguresSchema,
  tableSchema,
  noteSchema,
  hrSchema,
]);

/** Blocks as an array of unknowns; each one is parsed by {@link parseBlocks}. */
const rawBlocks = z.array(z.unknown());

export interface ParsedBlocks {
  blocks: ArticleBlock[];
  /** How many blocks were dropped as unknown or malformed. */
  dropped: number;
}

export function parseBlocks(raw: readonly unknown[]): ParsedBlocks {
  const blocks: ArticleBlock[] = [];
  let dropped = 0;
  for (const item of raw) {
    const parsed = articleBlockSchema.safeParse(item);
    if (parsed.success) blocks.push(parsed.data);
    else dropped += 1;
  }
  return { blocks, dropped };
}

const faqSchema = z.array(z.object({ q: text, a: text }));
const assetSizesSchema = z.record(
  z.string(),
  z.object({ width: z.number().int().positive(), height: z.number().int().positive() }),
);

// ── Public read API (§4) ────────────────────────────────────────────────────

const publicSummaryShape = {
  slug: text,
  title: text,
  kicker: z.string().nullish().transform((v) => v ?? null),
  description: text,
  lead: text,
  stand: text,
  series: z.string().nullish().transform((v) => v ?? null),
  country: z.string().default('DE'),
  published_at: text,
  updated_at: text,
  og_image: text,
};

export const publicArticleSummarySchema = z.object(publicSummaryShape);

export const publicArticleListSchema = z.object({
  articles: z.array(publicArticleSummarySchema),
});

export const publicArticleSchema = z.object({
  ...publicSummaryShape,
  seo_title: z.string().nullish().transform((v) => v ?? null),
  blocks: rawBlocks,
  faq: faqSchema.nullish().transform((v) => v ?? []),
  assets: assetSizesSchema.default({}),
});

export type SchemaIssues = z.ZodError['issues'];

export type ParseResult<T> =
  | { ok: true; value: T; dropped: number }
  | { ok: false; issues: SchemaIssues };

export function parsePublicArticleList(raw: unknown): ParseResult<PublicArticleSummary[]> {
  const parsed = publicArticleListSchema.safeParse(raw);
  if (!parsed.success) return { ok: false, issues: parsed.error.issues };
  return { ok: true, value: parsed.data.articles, dropped: 0 };
}

export function parsePublicArticle(raw: unknown): ParseResult<PublicArticle> {
  const parsed = publicArticleSchema.safeParse(raw);
  if (!parsed.success) return { ok: false, issues: parsed.error.issues };
  const { blocks, dropped } = parseBlocks(parsed.data.blocks);
  return { ok: true, value: { ...parsed.data, blocks }, dropped };
}

// ── Admin API (§3) ──────────────────────────────────────────────────────────

const revisionContentSchema = z.object({
  schema: z.literal('newsroom-1'),
  linkedin: z.object({
    text,
    hashtags: z.array(text).default([]),
    first_comment: text,
    image: text,
    image_alt: text,
  }),
  article: z.object({
    title: text,
    seo_title: optionalText,
    description: text,
    kicker: optionalText,
    lead: text,
    stand: text,
    og_image: text,
    blocks: rawBlocks,
    faq: faqSchema.nullish(),
  }),
  review_en: z
    .object({ linkedin_text: optionalText, article_summary: optionalText })
    .nullish(),
  facts: z
    .array(
      z.object({
        id: text,
        display: text,
        value: z.union([z.number(), z.string()]).nullish().transform((v) => v ?? null),
        label: text,
        query: text,
      }),
    )
    .nullish(),
});

const editionSummaryShape = {
  id: z.number().int(),
  slug: text,
  title: text,
  series: z.string().nullable(),
  country: text,
  language: text,
  status: z.enum(['draft', 'published', 'unpublished']),
  current_revision: z.number().int().nullable(),
  published_revision: z.number().int().nullable(),
  approved_by: z.string().nullable(),
  created_at: text,
  updated_at: text,
  research_published_at: z.string().nullable(),
  research_updated_at: z.string().nullable(),
  linkedin_url: z.string().nullable(),
  published_linkedin_at: z.string().nullable(),
  public_url: text,
};

export const editionListSchema = z.object({
  editions: z.array(z.object(editionSummaryShape)),
});

export const editionDetailSchema = z.object({
  ...editionSummaryShape,
  revisions: z.array(
    z.object({ n: z.number().int(), note: text, created_at: text, content: revisionContentSchema }),
  ),
  assets: z.array(
    z.object({
      id: text,
      content_type: text,
      size: z.number().int(),
      width: z.number().int(),
      height: z.number().int(),
      sha256: text,
      created_at: z.string().nullish().transform((v) => v ?? null),
    }),
  ),
  checks: z.object({ errors: z.array(text), warnings: z.array(text) }).nullable(),
  linkedin_resolved: z.object({ post_text: text, first_comment: text }).nullable(),
});

export function parseEditionList(raw: unknown): ParseResult<EditionList> {
  const parsed = editionListSchema.safeParse(raw);
  if (!parsed.success) return { ok: false, issues: parsed.error.issues };
  return { ok: true, value: parsed.data, dropped: 0 };
}

export function parseEditionDetail(raw: unknown): ParseResult<EditionDetail> {
  const parsed = editionDetailSchema.safeParse(raw);
  if (!parsed.success) return { ok: false, issues: parsed.error.issues };
  let dropped = 0;
  const revisions = parsed.data.revisions.map((revision) => {
    const blocks = parseBlocks(revision.content.article.blocks);
    dropped += blocks.dropped;
    const content: RevisionContent = {
      ...revision.content,
      article: { ...revision.content.article, blocks: blocks.blocks },
    };
    return { ...revision, content };
  });
  return { ok: true, value: { ...parsed.data, revisions }, dropped };
}
